import { useMemo, useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import type { BatchSummary } from "./bulkGrantTypes";

type Props = {
  target: BatchSummary | null;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
};

/** Typed-confirmation dialog for reversing a previous bulk credit grant. */
export const BulkReverseDialog = ({ target, busy, onCancel, onConfirm }: Props) => {
  const [reason, setReason] = useState("");
  const [confirmText, setConfirmText] = useState("");

  const reasonValid = reason.trim().length >= 5 && reason.trim().length <= 500;
  const canConfirm = reasonValid && confirmText === "REVERSE" && !busy;

  const totalToReverse = useMemo(() => {
    if (!target) return 0;
    return target.succeeded * target.amount;
  }, [target]);

  const reset = () => {
    setReason("");
    setConfirmText("");
  };

  return (
    <AlertDialog
      open={target !== null}
      onOpenChange={(o) => {
        if (!o) {
          reset();
          onCancel();
        }
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Reverse this batch?</AlertDialogTitle>
          <AlertDialogDescription>
            This will deduct{" "}
            <b>{target?.amount.toLocaleString() ?? "?"}</b> credits from each
            of the <b>{target?.succeeded ?? 0}</b> user(s) who received this
            grant — up to{" "}
            <b>{totalToReverse.toLocaleString()}</b> credits in total. Users
            whose current balance is below the granted amount will be left
            untouched and reported back. Type <b>REVERSE</b> to confirm.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-2">
          <div>
            <Label htmlFor="bulk-reverse-reason">Reason (required)</Label>
            <Textarea
              id="bulk-reverse-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder="e.g. Reverting accidental promo grant"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              {reason.trim().length}/500 · minimum 5 characters
            </p>
          </div>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="REVERSE"
          />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={!canConfirm}
            onClick={(e) => {
              e.preventDefault();
              const r = reason.trim();
              reset();
              onConfirm(r);
            }}
          >
            {busy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <AlertTriangle className="h-4 w-4" />
            )}{" "}
            Confirm reversal
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
